import React, { useState } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";

// Relative ranges rather than a date picker, for the reason the other three
// apps' equivalents record: react-datepicker is physically present under node_modules
// as an undeclared peer of @repoflow/form-schema that this app never asks for,
// and registering that widget would mean adding the dependency and re-resolving
// the lockfile — to replace a control every supported browser already ships as
// <input type="datetime-local">. And a relative range is what actually gets
// clicked: "the last hour" is the question, not a pair of calendar dates.
export const RANGES = [
  { key: "1h", label: "Last hour", ms: 60 * 60 * 1000 },
  { key: "24h", label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
  { key: "7d", label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
  { key: "30d", label: "Last 30 days", ms: 30 * 24 * 60 * 60 * 1000 },
  { key: "custom", label: "Custom..." },
];

// The server caps a window at 31 days, so no range here is one it would silently
// clamp. Keep that true if a longer option is ever added.
export const DEFAULT_RANGE = "24h";

export const rangeLabel = (range) =>
  (RANGES.find((r) => r.key === range) || {}).label || "Selected range";

// This point carries exactly four event types: "create" as a site, page,
// section, key or file appears, "remove" as one goes, "session" when a session
// is established, and "upload" when a file's BYTES land — which is a different
// moment from the create that wrote its row, and the only event that carries a
// byte figure. A free-text box would be a worse control than a list of the four
// that exist.
const EVENT_TYPES = [
  { key: "", label: "Any event" },
  { key: "create", label: "Created" },
  { key: "remove", label: "Removed" },
  { key: "session", label: "Sessions" },
  { key: "upload", label: "Uploads" },
];

export const buildFilter = ({ range, start, end, eventType }) => {
  const filter = {};

  if (range === "custom") {
    // Both are datetime-local values — no zone — so they are read as local time
    // and sent as ISO, which is what the server parses.
    if (start) filter.start = new Date(start).toISOString();
    if (end) filter.end = new Date(end).toISOString();
  } else {
    const { ms } = RANGES.find((r) => r.key === range) || {};
    if (ms) filter.start = new Date(Date.now() - ms).toISOString();
  }

  if (eventType) filter.eventType = eventType;

  // NO DIMENSION IS EVER PRODUCED HERE, and there is no input that could produce
  // one. `orgId` is supplied by the server from the resolved account and a
  // request carrying it is rejected rather than overridden — that dimension is
  // the entire tenancy boundary of this screen. `siteId` is not filterable
  // either: it is grouped by below, and a breakdown needs no filter. See the
  // tenancy note in the api's model/organization-statistics.
  return filter;
};

// Submit-only, like the site search box. Every apply re-runs a billed Timestream
// query behind two GraphQL hops, so filtering on keystroke would be expensive as
// well as noisy.
//
// One filter row above everything it scopes: the charts and the table below both
// re-render against the same slice, and neither carries a filter of its own.
export const Filter = ({ onApply }) => {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [eventType, setEventType] = useState("");

  const submit = (event) => {
    event.preventDefault();
    onApply({ filter: buildFilter({ range, start, end, eventType }), range });
  };

  return (
    <Form onSubmit={submit} className="mb-3">
      <Row form>
        <Col md={3}>
          <FormGroup>
            <Label for="capture-range">Range</Label>
            <Input
              id="capture-range"
              type="select"
              value={range}
              onChange={(e) => setRange(e.target.value)}
            >
              {RANGES.map(({ key, label }) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Input>
          </FormGroup>
        </Col>

        {range === "custom" && (
          <>
            <Col md={3}>
              <FormGroup>
                <Label for="capture-start">From</Label>
                <Input
                  id="capture-start"
                  type="datetime-local"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="capture-end">To</Label>
                <Input
                  id="capture-end"
                  type="datetime-local"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </FormGroup>
            </Col>
          </>
        )}

        <Col md={3}>
          <FormGroup>
            <Label for="capture-event-type">Event</Label>
            <Input
              id="capture-event-type"
              type="select"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            >
              {EVENT_TYPES.map(({ key, label }) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      <Button color="primary" type="submit">
        <i className="fa fa-search" /> Apply
      </Button>
    </Form>
  );
};
